import moment from 'moment';
import { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { Button } from '@/components/ui/daisy';
import { formatRecordDateTime } from '@/utils/assessments/recordDateTime';

import {
  RecordFormShellActionsProps,
  RecordFormShellContextValue,
  RecordFormShellDatetimeProps,
  RecordFormShellProps,
  RecordFormShellTitleProps,
} from './types';

const RecordFormShellContext = createContext<RecordFormShellContextValue>({
  hasChanges: false,
  onCancel: () => {},
});

const useRecordFormShellContext = () => useContext(RecordFormShellContext);

const RecordFormShellBase = ({
  className,
  hasChanges,
  isSaving,
  onCancel,
  children,
  ...props
}: RecordFormShellProps) => {
  const classes = twMerge('flex flex-col gap-4', className);

  return (
    <RecordFormShellContext.Provider value={{ hasChanges, isSaving, onCancel }}>
      <form className={classes} {...props}>
        {children}
      </form>
    </RecordFormShellContext.Provider>
  );
};

const Title = ({ className, children }: RecordFormShellTitleProps) => (
  <h3
    className={twMerge('font-poppins text-lg font-600 leading-none', className)}
  >
    {children}
  </h3>
);

const Datetime = ({ className, value }: RecordFormShellDatetimeProps) => (
  <p
    className={twMerge(
      'self-end text-sm leading-none text-base-content',
      className,
    )}
  >
    {formatRecordDateTime(value ?? moment().toISOString())}
  </p>
);

const Actions = ({ className, size = 'md' }: RecordFormShellActionsProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'common' });
  const { hasChanges, isSaving, onCancel } = useRecordFormShellContext();

  return (
    <div className={twMerge('flex gap-4', className)}>
      <Button type="button" variant="soft" onClick={onCancel} size={size}>
        {t('cancel')}
      </Button>
      <Button
        type="submit"
        color="primary"
        loading={isSaving}
        disabled={isSaving || !hasChanges}
        size={size}
      >
        {t('save')}
      </Button>
    </div>
  );
};

RecordFormShellBase.displayName = 'RecordFormShell';
Title.displayName = 'RecordFormShell.Title';
Datetime.displayName = 'RecordFormShell.Datetime';
Actions.displayName = 'RecordFormShell.Actions';

export const RecordFormShell = Object.assign(RecordFormShellBase, {
  Title,
  Datetime,
  Actions,
});
